import React from "react";
import { Server, Cloud, Database, Search, Loader } from "lucide-react";
import {
  DeviceResponse,
  CloudProviderResponse,
  AwsServiceTypeResponse,
  AwsResourceListResponse,
  AssetProtocol,
  AssetRole,
  AssetStatus,
} from "../../../../api/frameworksApi";
import { FormField, FormSelect, FormInput } from "../../../components/Modal";
import { autoFillPort, StatusBadge } from "../cmdbUtils";

export type InfraTargetType = "DEVICE" | "CLOUD";

export interface DeviceForm {
  targetType: InfraTargetType;
  deviceId: string;
  cloudProviderId: string;
  cloudResourceId: string;
  port: string;
  protocol: AssetProtocol;
  role: AssetRole;
  status: AssetStatus;
}

interface InfraPickerProps {
  search: string;
  onSearch: (v: string) => void;
  form: DeviceForm;
  onForm: (v: DeviceForm) => void;
  devices: DeviceResponse[];
  cloudAccounts: CloudProviderResponse[];
  awsResTypes: AwsServiceTypeResponse[];
  awsResTypeFilter: string;
  onResTypeFilter: (v: string) => void;
  awsResSearch: string;
  onResSearch: (v: string) => void;
  awsResources: AwsResourceListResponse[];
  loadingAwsRes: boolean;
  onCloudAccountChange: (providerId: string) => void;
}

const ROLE_OPTIONS: { value: AssetRole; label: string }[] = [
  { value: "PRIMARY", label: "Primary" },
  { value: "BACKUP", label: "Backup" },
  { value: "STANDBY", label: "Standby" },
  { value: "READ_REPLICA", label: "Read Replica" },
  { value: "DR", label: "DR" },
  { value: "ACTIVE", label: "Active" },
  { value: "PASSIVE", label: "Passive" },
];

const PROTOCOL_OPTIONS = [
  { value: "TCP", label: "TCP" },
  { value: "UDP", label: "UDP" },
  { value: "HTTP", label: "HTTP" },
  { value: "HTTPS", label: "HTTPS" },
  { value: "GRPC", label: "gRPC" },
];

const DEP_STATUS_OPTIONS: { value: AssetStatus; label: string }[] = [
  { value: "RUNNING", label: "Running" },
  { value: "STOPPED", label: "Stopped" },
  { value: "STARTING", label: "Starting" },
  { value: "DEGRADED", label: "Degraded" },
  { value: "MAINTENANCE", label: "Maintenance" },
  { value: "UNKNOWN", label: "Unknown" },
];

export function InfraPicker({
  search,
  onSearch,
  form,
  onForm,
  devices,
  cloudAccounts,
  awsResTypes,
  awsResTypeFilter,
  onResTypeFilter,
  awsResSearch,
  onResSearch,
  awsResources,
  loadingAwsRes,
  onCloudAccountChange,
}: InfraPickerProps) {
  const filteredDevs = devices.filter(
    (d) =>
      !search ||
      (d.name || "").toLowerCase().includes(search.toLowerCase()) ||
      (d.ipAddress || "").includes(search),
  );

  return (
    <div className="space-y-3">
      {/* Target type toggle */}
      <FormField label="Target Type" required>
        <div className="flex gap-2">
          {(["DEVICE", "CLOUD"] as InfraTargetType[]).map((t) => (
            <button
              key={t}
              onClick={() =>
                onForm({
                  ...form,
                  targetType: t,
                  deviceId: "",
                  cloudProviderId: "",
                  cloudResourceId: "",
                })
              }
              className={`flex-1 px-2 py-2 rounded-lg border text-xs font-medium transition-colors flex items-center justify-center gap-1.5 ${
                form.targetType === t
                  ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400"
                  : "border-border hover:bg-muted"
              }`}
            >
              {t === "DEVICE" ? (
                <>
                  <Server className="size-3.5" /> On-Prem
                </>
              ) : (
                <>
                  <Cloud className="size-3.5" /> Cloud Account
                </>
              )}
            </button>
          ))}
        </div>
      </FormField>

      {/* On-Prem Device picker */}
      {form.targetType === "DEVICE" && (
        <FormField label="Device" required>
          <div className="relative mb-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 d" />
            <input
              type="text"
              placeholder="Search devices…"
              value={search}
              onChange={(e) => onSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background focus:border-indigo-500 outline-none text-sm"
            />
          </div>
          <div className="max-h-40 overflow-y-auto space-y-1 border border-border rounded-lg p-1">
            {filteredDevs.map((d) => (
              <button
                key={d.id}
                onClick={() => onForm({ ...form, deviceId: d.id })}
                className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center gap-2 ${
                  form.deviceId === d.id
                    ? "bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 border border-indigo-300"
                    : "hover:bg-muted"
                }`}
              >
                <Server className="size-3.5 flex-shrink-0 d" />
                <span className="font-medium truncate">{d.name}</span>
                <span className="text-xs d flex-shrink-0">
                  {d.ipAddress ?? "—"}
                </span>
              </button>
            ))}
            {filteredDevs.length === 0 && (
              <p className="text-center text-xs d py-3">No devices found</p>
            )}
          </div>
        </FormField>
      )}

      {/* Cloud Account → Resource Type → Search → Resource list */}
      {form.targetType === "CLOUD" && (
        <>
          <FormField label="Cloud Account" required>
            <FormSelect
              value={form.cloudProviderId}
              onChange={(v) => {
                onForm({ ...form, cloudProviderId: v, cloudResourceId: "" });
                onCloudAccountChange(v);
              }}
              options={[
                { value: "", label: "Select cloud account…" },
                ...cloudAccounts.map((a) => ({
                  value: a.id,
                  label: `${a.accountName} (${a.provider} · ${a.accountId})`,
                })),
              ]}
            />
          </FormField>

          {form.cloudProviderId && (
            <>
              {/* Resource type filter + search row */}
              <div className="flex gap-2">
                <div className="w-40 flex-shrink-0">
                  <FormSelect
                    value={awsResTypeFilter}
                    onChange={onResTypeFilter}
                    options={[
                      { value: "", label: "All Types" },
                      ...awsResTypes.map((t) => ({
                        value: t.cloudIdentifier || t.awsIdentifier || "",
                        label: t.serviceName,
                      })),
                    ]}
                  />
                </div>
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 d" />
                  <input
                    type="text"
                    placeholder="Search resources…"
                    value={awsResSearch}
                    onChange={(e) => onResSearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border border-border rounded-lg bg-background focus:border-indigo-500 outline-none text-sm"
                  />
                </div>
              </div>

              {/* Resource list */}
              <FormField label="Cloud Resource" required>
                {loadingAwsRes ? (
                  <div className="flex items-center justify-center py-10 d gap-2 border border-border rounded-lg">
                    <Loader className="size-4 animate-spin text-indigo-500" />
                    <span className="text-xs font-medium">
                      Searching cloud resources…
                    </span>
                  </div>
                ) : awsResources.length === 0 ? (
                  <div className="text-center py-10 d text-xs italic border border-border rounded-lg">
                    No resources found. Try a different search.
                  </div>
                ) : (
                  <div className="max-h-48 overflow-y-auto space-y-1 border border-border rounded-lg p-1">
                    {awsResources.map((r) => (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => {
                          const port = autoFillPort(
                            r.cloudIdentifier || r.awsIdentifier || "",
                            form.port,
                          );
                          onForm({ ...form, cloudResourceId: r.id, port });
                        }}
                        className={`w-full text-left px-3 py-2 rounded-md transition-colors flex items-center gap-2.5 ${
                          form.cloudResourceId === r.id
                            ? "bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-300 dark:border-indigo-500/40"
                            : "hover:bg-muted"
                        }`}
                      >
                        <Database className="size-3.5 flex-shrink-0 d" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {r.resourceName || r.resourceId}
                          </p>
                          {r.resourceName && (
                            <p className="text-[10px] font-mono d truncate">
                              {r.resourceId}
                            </p>
                          )}
                          <p className="text-[10px] d truncate">
                            {r.serviceName} · {r.region}
                            {r.availabilityZone
                              ? ` · ${r.availabilityZone}`
                              : ""}
                          </p>
                        </div>
                        {r.status && (
                          <div className="flex-shrink-0 scale-75">
                            <StatusBadge status={r.status} />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
                {form.cloudResourceId &&
                  (() => {
                    const sel = awsResources.find(
                      (r) => r.id === form.cloudResourceId,
                    );
                    return sel ? (
                      <p className="mt-1.5 text-xs text-indigo-600 dark:text-indigo-400 font-medium px-1">
                        ✓ {sel.resourceName || sel.resourceId}
                      </p>
                    ) : null;
                  })()}
              </FormField>
            </>
          )}
        </>
      )}

      {/* Common fields */}
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Port">
          <FormInput
            type="text"
            value={form.port}
            onChange={(v) => onForm({ ...form, port: v.replace(/\D/g, "") })}
            placeholder="e.g. 3306"
          />
        </FormField>
        <FormField label="Role" required>
          <FormSelect
            value={form.role}
            onChange={(v) => onForm({ ...form, role: v as AssetRole })}
            options={ROLE_OPTIONS}
          />
        </FormField>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Protocol">
          <FormSelect
            value={form.protocol}
            onChange={(v) => onForm({ ...form, protocol: v as AssetProtocol })}
            options={PROTOCOL_OPTIONS}
          />
        </FormField>
        <FormField label="Status">
          <FormSelect
            value={form.status}
            onChange={(v) => onForm({ ...form, status: v as AssetStatus })}
            options={DEP_STATUS_OPTIONS}
          />
        </FormField>
      </div>
    </div>
  );
}
